/* RNRK9 — extended Dawn CartNotification: centered "Complete Your Order" modal
   with subtotal, free-shipping status, and "Goes well with" recommendations.

   "Goes well with" cards are server-rendered by sections/rnrk9-cart-upsell-card.liquid
   (same card, colour swatches and quick add as the home page) and fetched here through
   the Section Rendering API. Quick add inside the modal posts to /cart/add with the
   modal's sections so the product line, subtotal, item count and cart icon refresh in
   place without closing the modal. */
class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.FREE_SHIPPING_THRESHOLD_CENTS = 9900; /* keep in sync with snippets/cart-drawer.liquid + main-cart-footer */
    this.UPSELL_CARD_SECTION = 'rnrk9-cart-upsell-card'; /* sections/rnrk9-cart-upsell-card.liquid */

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.recommendationRenderToken = 0;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    /* Quick add on a "Goes well with" card (buttons are injected after each add,
       so delegate from the element). */
    this.addEventListener('click', this.onQuickAddClick.bind(this));

    /* BSS B2B rewrites [bss-b2b-cart-total-price] client-side for wholesale
       customers. Watch the subtotal element and recompute the free-shipping
       line from whatever total is actually displayed. */
    const subtotalEl = this.querySelector('[data-cart-notification-subtotal]');
    if (subtotalEl) {
      new MutationObserver(() => this.syncShippingFromDisplayedTotal()).observe(subtotalEl, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  parseMoneyCents(text) {
    const m = String(text).replace(/,/g, '').match(/\d+(\.\d+)?/);
    return m ? Math.round(parseFloat(m[0]) * 100) : null;
  }

  syncShippingFromDisplayedTotal() {
    const subtotalEl = this.querySelector('[data-cart-notification-subtotal]');
    const shippingEl = this.querySelector('[data-cart-notification-shipping]');
    if (!subtotalEl || !shippingEl) return;
    const cents = this.parseMoneyCents(subtotalEl.textContent);
    if (cents === null) return;
    const remaining = this.FREE_SHIPPING_THRESHOLD_CENTS - cents;
    const done = remaining <= 0;
    const newText = done
      ? '✓ You’ve unlocked free shipping'
      : `${this.formatMoney(remaining)} away from free shipping`;
    if (shippingEl.textContent !== newText) {
      shippingEl.textContent = newText;
      shippingEl.classList.toggle('cart-notification__shipping-status--done', done);
    }
    shippingEl.hidden = false;
  }

  open() {
    this.notification.classList.add('animate', 'active');
    document.documentElement.classList.add('cart-notification-open');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.documentElement.classList.remove('cart-notification-open');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.renderSections(parsedState.sections);

    this.updateSummary();
    this.renderRecommendations(parsedState.product_id, parsedState);

    if (this.header) this.header.reveal();
    this.open();
  }

  /* Morph the three cart sections (product line, "View cart (n)" button, header
     cart icon) from a /cart/add sections response. */
  renderSections(sections) {
    if (!sections) return;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      const html = sections[section.id];
      if (!target || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  updateSummary() {
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const subtotalEl = this.querySelector('[data-cart-notification-subtotal]');
        if (subtotalEl) subtotalEl.textContent = this.formatMoney(cart.total_price);

        const countEl = this.querySelector('[data-cart-notification-count]');
        if (countEl) countEl.textContent = `${cart.item_count} Item${cart.item_count === 1 ? '' : 's'}`;

        this.syncShippingFromDisplayedTotal();
      })
      .catch((e) => console.error(e));
  }

  /* ── Quick add from a "Goes well with" card ─────────────────────────────────
     The card's colour dots (assets/rnrk9-card-swatches.js) swap the button's
     data-variant-id, so this just adds whatever variant the button points at.
     The modal stays open; only its product line / counts / subtotal refresh.
     The recommendation grid is left as-is so it doesn't reshuffle under the
     customer's cursor. */
  onQuickAddClick(evt) {
    const button = evt.target.closest('[data-cart-notification-quick-add]');
    if (!button || !this.contains(button)) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.quickAdd(button);
  }

  quickAdd(button) {
    const variantId = parseInt(button.getAttribute('data-variant-id'), 10);
    if (!variantId || button.disabled) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const body = {
      id: variantId,
      quantity: 1,
      sections: this.getSectionsToRender()
        .map((section) => section.id)
        .join(','),
      sections_url: window.location.pathname,
    };

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || 'Cart error');
        }

        this.cartItemKey = response.key;
        this.renderSections(response.sections);
        this.updateSummary();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-notification',
            productVariantId: variantId,
            cartData: response,
          });
        }
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        button.textContent = 'Added ✓';
        button.classList.add('is-added');
        setTimeout(() => {
          button.textContent = original;
          button.classList.remove('is-added');
          button.disabled = false;
        }, 1800);
      })
      .catch((e) => {
        console.error(e);
        button.textContent = 'Error — try again';
        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 2000);
      });
  }

  /* ── "Goes well with" rules ─────────────────────────────────────────────────────
     1. Always two in-stock toys from Training & Bite Work (collection: toys).
     2. Collar added  -> two leashes.   Leash added -> two collars.
     3. Anything else -> Shopify recommendations, minus the same product type.
     Never the product just added, never its own product type.
     Wholesale (WS-tagged) items pull from the wholesale collection instead. */
  renderRecommendations(productId, parsedState) {
    if (!productId) return;
    if (this.lastRecommendedProductId === productId) return;
    this.lastRecommendedProductId = productId;

    const wrapper = this.querySelector('[data-cart-notification-recommendations]');
    const grid = this.querySelector('[data-cart-notification-recommendations-grid]');
    if (!wrapper || !grid) return;

    const renderToken = ++this.recommendationRenderToken;

    const addedHandle = parsedState && parsedState.handle;
    const addedType = ((parsedState && parsedState.product_type) || '').toLowerCase();

    const toCents = (v) => (typeof v === 'number' ? v : Math.round(parseFloat(String(v).replace(/,/g, '')) * 100)) || 0;
    const normalizeCollectionProduct = (p) => ({
      handle: p.handle,
      url: `/products/${p.handle}`,
      title: p.title,
      type: (p.product_type || '').toLowerCase(),
      image: p.images && p.images[0] ? p.images[0].src : null,
      price: Math.min(...p.variants.map((v) => toCents(v.price))),
      compare_at_price: Math.max(...p.variants.map((v) => toCents(v.compare_at_price || 0))),
      available: p.variants.some((v) => v.available),
    });
    const normalizeRecommendation = (p) => ({
      handle: p.handle,
      url: p.url,
      title: p.title,
      type: (p.type || '').toLowerCase(),
      image: p.featured_image || null,
      price: p.price,
      compare_at_price: p.compare_at_price || 0,
      available: p.available !== false,
    });
    const shuffle = (arr) => arr.map((x) => [Math.random(), x]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);
    const fetchJson = (url, fallback) => fetch(url).then((r) => (r.ok ? r.json() : fallback)).catch(() => fallback);
    const fetchCollection = (handle) =>
      fetchJson(`/collections/${handle}/products.json?limit=50`, { products: [] }).then((d) => (d.products || []).map(normalizeCollectionProduct));

    (addedHandle ? fetchJson(`/products/${addedHandle}.js`, {}) : Promise.resolve({})).then((added) => {
      const isWholesale = !!(added && Array.isArray(added.tags) && added.tags.includes('WS'));
      const source = isWholesale
        ? fetchCollection('wholesale').then((all) => ({
            toys: all.filter((p) => p.type === 'dog toy'),
            leashes: all.filter((p) => p.type === 'leash'),
            collars: all.filter((p) => p.type === 'collar'),
          }))
        : Promise.all([fetchCollection('toys'), fetchCollection('leashes'), fetchCollection('collars')]).then(
            ([toys, leashes, collars]) => ({ toys, leashes, collars })
          );

      return source.then((pools) => {
        const seen = new Set([addedHandle]);
        const eligible = (p, allowSameType) => p.available && !seen.has(p.handle) && (allowSameType || p.type !== addedType);
        const take = (list, n, allowSameType) => {
          const picked = [];
          for (const p of shuffle(list)) {
            if (picked.length >= n) break;
            if (!eligible(p, allowSameType)) continue;
            seen.add(p.handle);
            picked.push(p);
          }
          return picked;
        };

        /* Rule 1: two toys. When the added item is itself a toy, other toys are still allowed here (just never the same product). */
        const toysPicked = take(pools.toys, 2, addedType === 'dog toy');

        /* Rules 2/3 */
        let pairPromise;
        if (addedType === 'collar') {
          pairPromise = Promise.resolve(take(pools.leashes, 4 - toysPicked.length));
        } else if (addedType === 'leash') {
          pairPromise = Promise.resolve(take(pools.collars, 4 - toysPicked.length));
        } else {
          pairPromise = fetchJson(`/recommendations/products.js?product_id=${productId}&limit=10&intent=complementary`, { products: [] })
            .then((d) => (d.products && d.products.length ? d : fetchJson(`/recommendations/products.js?product_id=${productId}&limit=10&intent=related`, { products: [] })))
            .then((d) => take((d.products || []).map(normalizeRecommendation), 4 - toysPicked.length))
            .then((picked) => {
              /* Top up from leashes + collars if recommendations came back thin */
              if (picked.length < 4 - toysPicked.length) {
                picked = picked.concat(take(shuffle(pools.leashes.concat(pools.collars)), 4 - toysPicked.length - picked.length));
              }
              return picked;
            });
        }

        return pairPromise.then((pair) => toysPicked.concat(pair));
      });
    })
      .then((products) => {
        if (!products.length) {
          if (renderToken === this.recommendationRenderToken) wrapper.hidden = true;
          return;
        }
        return this.renderRecommendationCards(products.slice(0, 4), renderToken);
      })
      .catch(() => {
        if (renderToken === this.recommendationRenderToken) wrapper.hidden = true;
      });
  }

  /* Fetch each card from sections/rnrk9-cart-upsell-card.liquid so it is the same
     card (swatches, badges, store money format) as the home page. If a section
     render fails, that product falls back to a plain link card. */
  renderRecommendationCards(products, renderToken) {
    const wrapper = this.querySelector('[data-cart-notification-recommendations]');
    const grid = this.querySelector('[data-cart-notification-recommendations-grid]');
    if (!wrapper || !grid) return;

    const fetchCard = (product) => {
      const base = String(product.url || `/products/${product.handle}`).split('?')[0];
      return fetch(`${base}?section_id=${this.UPSELL_CARD_SECTION}`, { headers: { Accept: 'text/html' } })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`${response.status}`))))
        .then((html) => {
          const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-upsell-card]');
          return card ? card.outerHTML : this.fallbackCardHtml(product);
        })
        .catch(() => this.fallbackCardHtml(product));
    };

    return Promise.all(products.map(fetchCard)).then((cards) => {
      /* A newer add has started its own render since this one began — drop it. */
      if (renderToken !== this.recommendationRenderToken) return;
      grid.innerHTML = cards.join('');
      wrapper.hidden = false;
    });
  }

  fallbackCardHtml(product) {
    const imageUrl = product.image ? product.image + (product.image.includes('?') ? '&' : '?') + 'width=360' : null;
    const image = imageUrl ? `<img src="${imageUrl}" alt="" loading="lazy" width="180" height="180">` : '';
    return `
      <a href="${product.url}" class="cart-notification__recommendation">
        <div class="cart-notification__recommendation-media">${image}</div>
        <p class="cart-notification__recommendation-title">${this.escapeHtml(product.title)}</p>
        <p class="cart-notification__recommendation-price">
          ${product.compare_at_price > product.price ? `<s>${this.formatMoney(product.compare_at_price)}</s> ` : ''}${this.formatMoney(product.price)}
        </p>
      </a>`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const match = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return match ? match.innerHTML : null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
